import { Button, Stack, Typography } from '@mui/material';
import { PeriodTypeEnum } from '@schooly/api';
import { PlusIcon, SmallButton, Toast } from '@schooly/style';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  TimetableStructureContextProps,
  TimetableStructureForm,
  TimetableStructurePeriodForm,
} from '../../../../../context/timetableStructure/WithTimetableStructure';
import {
  getMinutesDuration,
  hasTimeGap,
  hasTimeOverlap,
  minutesToTime,
  timeToMinutes,
} from '../../utils';
import { deriveRegisterSessions } from '../../registration-helpers';
import { TimetableStructurePeriodModal } from './TimetableStructurePeriodModal';
import { TimetableStructurePeriodRow } from './TimetableStructurePeriodRow';
import { createNewPeriod } from './utils';

interface TimetableStructurePeriodsListProps {
  confirmChange: TimetableStructureContextProps['confirmChange'];
}

export const TimetableStructurePeriodsList: FC<TimetableStructurePeriodsListProps> = ({
  confirmChange,
}) => {
  const { formatMessage } = useIntl();
  const { control, formState, watch, trigger } = useFormContext<TimetableStructureForm>();

  const { fields, replace } = useFieldArray({
    control,
    name: 'periods',
    rules: { required: formatMessage({ id: 'school-timetableStructures-Error-NoPeriods' }) },
  });

  const periods = watch('periods');

  const [editingPeriod, setEditingPeriod] = useState<TimetableStructurePeriodForm | null>(null);

  const validateOverlap = useCallback(
    (index: number) => {
      const currentPeriod = periods[index];
      const prevPeriod = periods[index - 1];
      const nextPeriod = periods[index + 1];

      if (prevPeriod && hasTimeOverlap(prevPeriod.end_time, currentPeriod.start_time)) {
        return formatMessage({ id: 'school-timetableStructure-Error-TimeOverlap' });
      }

      if (nextPeriod && hasTimeOverlap(currentPeriod.end_time, nextPeriod.start_time)) {
        return formatMessage({ id: 'school-timetableStructure-Error-TimeOverlap' });
      }

      return true;
    },
    [periods, formatMessage],
  );

  const validateGap = useCallback(
    (index: number) => {
      const currentPeriod = periods[index];
      const nextPeriod = periods[index + 1];

      if (nextPeriod && hasTimeGap(currentPeriod.end_time, nextPeriod.start_time)) {
        return formatMessage({ id: 'school-timetableStructure-Error-TimeGap' });
      }

      return true;
    },
    [periods, formatMessage],
  );

  const getNextPeriodTimes = () => {
    if (!periods.length) return { start_time: '', end_time: '' };

    const lastPeriod = periods[periods.length - 1];
    const startTime = lastPeriod.end_time;
    const prevLessonPeriod = periods.findLast((p) => p.type === PeriodTypeEnum.Lesson);
    if (!prevLessonPeriod) return { start_time: startTime, end_time: '' };

    const duration = getMinutesDuration(prevLessonPeriod.start_time, prevLessonPeriod.end_time);
    const endTime = minutesToTime(timeToMinutes(startTime) + duration);
    return { start_time: startTime, end_time: endTime };
  };

  const handleAddPeriodClick = (start_time: string, end_time: string) => {
    setEditingPeriod(createNewPeriod(start_time, end_time));
  };

  const handleEditPeriodClick = useCallback(
    (index: number) => {
      setEditingPeriod(periods[index]);
    },
    [periods],
  );

  const handleModalClose = () => {
    setEditingPeriod(null);
  };

  const handleDeletePeriod = useCallback(
    async (index: number) => {
      const confirmed = await confirmChange({
        type: 'remove_period',
        id: periods[index].id,
      });
      if (!confirmed) return;
      const remaining = periods.filter((_, i) => i !== index);
      replace(deriveRegisterSessions(remaining));
    },
    [periods, confirmChange, replace],
  );

  const handleModalSubmit = (period: TimetableStructurePeriodForm) => {
    const existingIndex = periods.findIndex((p) => p.id === period.id);
    const isNewPeriod = existingIndex === -1;
    const updatedPeriods = isNewPeriod
      ? [...periods, period]
      : periods.map((p) => (p.id === period.id ? period : p));
    const sortedPeriods = [...updatedPeriods].sort(
      (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time),
    );
    replace(deriveRegisterSessions(sortedPeriods));
    setEditingPeriod(null);
  };

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name === 'periods') {
        trigger('periods');
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  return (
    <Stack gap={1}>
      {fields.map((field, index) => {
        const periodError = formState.errors.periods?.[index];
        const isGapError = periodError?.type === 'gap';
        const isOverlapError = periodError?.type === 'overlap';

        return (
          <Fragment key={field.id}>
            <Controller
              control={control}
              name={`periods.${index}`}
              rules={{
                validate: {
                  overlap: () => validateOverlap(index),
                  gap: () => validateGap(index),
                },
              }}
              render={({ field: periodField }) => (
                <TimetableStructurePeriodRow
                  period={periodField.value}
                  onDelete={() => handleDeletePeriod(index)}
                  onEdit={() => handleEditPeriodClick(index)}
                  errorMessage={isOverlapError ? periodError?.message : undefined}
                />
              )}
            />
            {isGapError && (
              <Toast
                sx={{ py: 1, maxWidth: '100%', mr: 4.5, backgroundColor: 'error.main' }}
                message={periodError?.message}
                typographyProps={{ textAlign: 'left', whiteSpace: 'normal' }}
                actionsSlot={
                  <SmallButton
                    onClick={() =>
                      handleAddPeriodClick(periods[index].end_time, periods[index + 1].start_time)
                    }
                    startIcon={<PlusIcon />}
                  >
                    {formatMessage({ id: 'school-timetableStructures-AddPeriod' })}
                  </SmallButton>
                }
              />
            )}
          </Fragment>
        );
      })}
      <Stack gap={0.25} alignItems="flex-start">
        <Button
          startIcon={<PlusIcon />}
          variant="outlined"
          onClick={() => {
            const times = getNextPeriodTimes();
            handleAddPeriodClick(times.start_time, times.end_time);
          }}
        >
          <FormattedMessage id="school-timetableStructures-AddPeriod" />
        </Button>
        {formState.errors.periods?.root && (
          <Typography variant="caption" color="error.main">
            {formState.errors.periods.root.message}
          </Typography>
        )}
      </Stack>

      {editingPeriod && (
        <TimetableStructurePeriodModal
          period={editingPeriod}
          registrationDisabled={
            periods.filter((p) => p.id !== editingPeriod.id && p.register_session != null).length >=
            2
          }
          confirmChange={confirmChange}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
        />
      )}
    </Stack>
  );
};
